import React from 'react';
import { Link } from 'react-router-dom';
import { Plane, Truck, Warehouse, ArrowUpRight, Package } from 'lucide-react';
import { heroImages, heroCards } from '../mock/data';
import { useSiteSettings } from '../context/SiteSettingsContext';

const icons = { Plane, Truck, Warehouse };

const Hero = () => {
  const { settings } = useSiteSettings();
  const eyebrow = settings?.hero_eyebrow || 'Premium Global Logistics • Est. 2014';
  const line1 = settings?.hero_title_line1 || 'We deliver happiness,';
  const line2 = settings?.hero_title_line2 || 'one parcel at a time.';
  const subtitle = settings?.hero_subtitle || 'Eleven years of moving what matters most — for families across Europe, Sri Lanka and beyond. A one-stop logistics partner built on trust, precision and care.';
  const heroBg = settings?.image_hero_bg || heroImages.bg;
  const hasHappiness = /happiness/i.test(line1);
  const titleLine1 = hasHappiness ? line1.split(/happiness/i)[0] : line1;
  const titleLine1Tail = hasHappiness ? line1.substring(titleLine1.length + 'happiness'.length) : '';
  return (
    <section className="relative overflow-hidden bg-white">
      <div className="relative min-h-[680px] flex items-center">
        <div className="absolute inset-0">
          <img src={heroBg} alt="Global logistics" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/95 via-black/70 to-black/20" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 py-24 w-full">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-3 mb-6">
              <span className="h-px w-10 bg-[#c8102e]" />
              <span className="eyebrow text-white/90">{eyebrow}</span>
            </div>
            <h1 className="text-white font-serif-display font-bold text-[52px] md:text-[72px] leading-[1.02] mb-8 tracking-tight">
              {hasHappiness ? (
                <>{titleLine1}<span className="italic text-[#c8102e]">happiness</span>{titleLine1Tail}<br />{line2}</>
              ) : (
                <>{line1}<br />{line2}</>
              )}
            </h1>
            <p className="text-white/80 text-lg mb-10 max-w-xl leading-relaxed">
              {subtitle}
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/services" className="group inline-flex items-center gap-3 bg-[#c8102e] hover:bg-white hover:text-black text-white font-semibold h-13 px-8 py-4 text-[14px] tracking-[0.15em] uppercase transition-colors">
                Our Services <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              </Link>
              <Link to="/find-branch" className="group inline-flex items-center gap-3 border border-white/40 hover:border-white text-white font-semibold h-13 px-8 py-4 text-[14px] tracking-[0.15em] uppercase transition-colors">
                Find a Branch <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              </Link>
            </div>
          </div>
        </div>

        {/* Side stat card */}
        <div className="hidden lg:block absolute right-8 top-1/2 -translate-y-1/2 z-10">
          <div className="bg-white p-6 shadow-2xl w-64 border-l-4 border-[#c8102e]">
            <div className="font-serif-display text-5xl font-bold text-black">40<span className="text-[#c8102e]">+</span></div>
            <div className="text-xs tracking-[0.25em] uppercase text-neutral-500 mt-2 mb-3">Global Branches</div>
            <div className="h-px bg-neutral-200 my-3" />
            <p className="text-xs text-neutral-600 leading-relaxed">Across Sri Lanka, Italy, France, Romania and the United Kingdom — serving thousands every week.</p>
          </div>
        </div>
      </div>

      {/* Service Cards */}
      <div className="relative z-20 -mt-14 max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-0 bg-white shadow-2xl">
          {heroCards.map((card, idx) => {
            const Icon = icons[card.icon] || Package;
            return (
              <div key={card.title} className={`group p-8 lg:p-10 relative ${idx !== 2 ? 'md:border-r border-neutral-100' : ''} hover:bg-black hover:text-white transition-colors duration-300`}>
                <div className="w-14 h-14 rounded-none border border-neutral-200 group-hover:border-[#c8102e] flex items-center justify-center mb-6 transition-colors">
                  <Icon className="w-6 h-6 text-[#c8102e]" />
                </div>
                <h3 className="font-serif-display text-2xl font-bold mb-3">{card.title}</h3>
                <p className="text-sm text-neutral-600 group-hover:text-white/70 leading-relaxed mb-5">{card.desc}</p>
                <Link to="/services" className="inline-flex items-center gap-2 text-[#c8102e] font-semibold text-[13px] uppercase tracking-[0.15em] hover:gap-3 transition-all">
                  Learn More <ArrowUpRight className="w-4 h-4" />
                </Link>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Hero;
